import { GuestReservationDto, LanguageEnum, LayoutType, ReservationOrigin, ReservationState } from 'gleblu-socket-api';
import { Map } from 'immutable';
import { createReducer } from 'redux-act';

import { AnyReservation, ReservationSeating } from '@/models/interfaces/reservation/Reservation';
import { authenticationRequired } from '@/store/actions/authentication/authenticationRequired';
import { logout } from '@/store/actions/authentication/logout';
import { layoutDeleted } from '@/store/actions/layout/deleteLayout';
import { layoutSaved } from '@/store/actions/layout/saveLayout';
import { reservationAccepted } from '@/store/actions/reservation/acceptReservation';
import { reservationCanceled } from '@/store/actions/reservation/cancelReservation';
import { reservationCreated } from '@/store/actions/reservation/createReservation';
import { reservationsFetched } from '@/store/actions/reservation/fetchReservations';
import { guestArrivedSaved } from '@/store/actions/reservation/guestArrived';
import {
  acceptRealTimeReservation,
  controlRequestArrived,
  newRealTimeReservationRequest,
  sendRequestReservationAnswerError,
  sendRequestReservationAnswerSuccess,
  sendReservationRequestResponse,
} from '@/store/actions/reservation/realTimeReservation';
import { reservationRejected } from '@/store/actions/reservation/rejectReservation';
import { reservationResolved } from '@/store/actions/reservation/resolveReservation';
import { reservationUpdated } from '@/store/actions/reservation/updateReservation';
import { reservationStateUpdated } from '@/store/actions/reservation/updateReservationState';
import { specialHourAdded } from '@/store/actions/setting/addSpecialHour';
import { specialHourDeleted } from '@/store/actions/setting/deleteSpecialHour';
import { specialHourModified } from '@/store/actions/setting/modifySpecialHour';
import { regularHoursSaved } from '@/store/actions/setting/saveHours';
import { addEntity, replaceEntity } from '@/utils/entityUtils';

export interface ReservationsState {
  reservations: AnyReservation[];
  suggestedSeatings: Map<string, ReservationSeating>;
  requestArrived: boolean;
  requestAccepted: boolean;
  answerSent: boolean;
  answerSentError: boolean;
  answerSentSuccess: boolean;
  isLoading: boolean;
  realTimeReservations: GuestReservationDto[];
}

const defaultState: ReservationsState = {
  reservations: [],
  suggestedSeatings: Map<string, ReservationSeating>(),
  requestArrived: false,
  requestAccepted: false,
  answerSent: false,
  answerSentError: false,
  answerSentSuccess: false,
  isLoading: false,
  realTimeReservations: [
    {
      reservationState: ReservationState.pending,
      guest: {
        uuid: '0',
        reservationUuid: '0',
        phoneNumber: '0000000000',
        firstName: 'John',
        lastName: 'Doe',
        email: 'email',
        selectedLanguage: LanguageEnum.ENGLISH,
      },
      origin: ReservationOrigin.guestWeb,
      uuid: '0',
      placeUuid: '0',
      date: '2000-01-01',
      realTime: false,
      layoutType: LayoutType.indoor,
      numberOfGuests: 0,
      specialRequest: 'Hamburger please',
    },
  ],
};

const reservationReducer = createReducer<ReservationsState>({}, defaultState);

reservationReducer.on(reservationsFetched, (state, { placeUuid, reservations }) => ({
  ...state,
  reservations: [...state.reservations.filter((reservation) => reservation.placeUuid !== placeUuid), ...reservations],
}));

reservationReducer.on(reservationCreated, (state, { reservation }) => ({
  ...state,
  reservations: addEntity(state.reservations, reservation),
}));

reservationReducer.on(reservationStateUpdated, (state, { reservation }) => ({
  ...state,
  reservations: replaceEntity(state.reservations, reservation),
}));

reservationReducer.on(reservationUpdated, (state, { reservation }) => ({
  ...state,
  reservations: replaceEntity(state.reservations, reservation),
}));

reservationReducer.on(reservationCanceled, (state, { reservationUuid }) => ({
  ...state,
  reservations: state.reservations.filter(({ uuid }) => uuid !== reservationUuid),
}));

reservationReducer.on(guestArrivedSaved, (state, { reservation }) => ({
  ...state,
  reservations: replaceEntity(state.reservations, reservation),
}));

reservationReducer.on(reservationRejected, (state, { reservation }) => ({
  ...state,
  reservations: state.reservations.filter((r) => r.uuid !== reservation.uuid),
  requestArrived: false,
}));

reservationReducer.on(reservationAccepted, (state, { reservation }) => ({
  ...state,
  reservations: replaceEntity(state.reservations, reservation),
  requestArrived: false,
}));

reservationReducer.on(reservationResolved, (state, { reservation }) => ({
  ...state,
  reservations: replaceEntity(state.reservations, reservation),
}));

function replaceReservations(initialReservations: AnyReservation[], replaceWith: AnyReservation[]): AnyReservation[] {
  return replaceWith.reduce((reservations, affectedReservation) => replaceEntity(reservations, affectedReservation), initialReservations);
}

reservationReducer.on(regularHoursSaved, (state, { affectedReservations }) => ({
  ...state,
  reservations: replaceReservations(state.reservations, affectedReservations),
}));

reservationReducer.on(specialHourAdded, (state, { affectedReservations }) => ({
  ...state,
  reservations: replaceReservations(state.reservations, affectedReservations),
}));

reservationReducer.on(specialHourModified, (state, { affectedReservations }) => ({
  ...state,
  reservations: replaceReservations(state.reservations, affectedReservations),
}));

reservationReducer.on(specialHourDeleted, (state, { affectedReservations }) => ({
  ...state,
  reservations: replaceReservations(state.reservations, affectedReservations),
}));

reservationReducer.on(layoutSaved, (state, { affectedReservations }) => ({
  ...state,
  reservations: replaceReservations(state.reservations, affectedReservations),
}));

reservationReducer.on(layoutDeleted, (state, { affectedReservations }) => ({
  ...state,
  reservations: replaceReservations(state.reservations, affectedReservations),
}));

reservationReducer.on(logout, () => defaultState);

reservationReducer.on(authenticationRequired, () => defaultState);

reservationReducer.on(newRealTimeReservationRequest, (state, dto) => ({
  ...state,
  requestArrived: true,
  answerSent: false,
  realTimeReservation: dto,
}));

reservationReducer.on(acceptRealTimeReservation, (state, reservationDto) => ({
  ...state,
  requestAccepted: true,
  requestArrived: false,
  reservations: replaceEntity(state.reservations, reservationDto),
}));

reservationReducer.on(sendReservationRequestResponse, (state, { accepted }) => ({
  ...state,
  requestAccepted: accepted,
  requestArrived: false,
  answerSent: true,
  isLoading: true,
}));

reservationReducer.on(sendRequestReservationAnswerError, (state) => ({
  ...state,
  isLoading: false,
  answerSentError: true,
  requestArrived: false,
}));

reservationReducer.on(controlRequestArrived, (state, { requestArrived }) => ({
  ...state,
  requestArrived: requestArrived,
}));

reservationReducer.on(sendRequestReservationAnswerSuccess, (state) => ({
  ...state,
  isLoading: false,
  answerSentSuccess: true,
  requestArrived: false,
}));

export default reservationReducer;
